import { API_CONFIG, buildApiUrl } from '../config/api';

// Types pour les réponses API
export interface ApiResponse<T> {
  data?: T;
  error?: string;
  success: boolean;
}

/** Compteurs de la page d'accueil, recalculés côté serveur à chaque appel. */
export interface PublicStats {
  formationStudents: number;
  competitionParticipants: number;
  formations: number;
  projects: number;
}

/** Charge utile du checkout. */
export interface CheckoutPayload {
  items: { productId: string; quantity: number }[];
  customer: { name: string; email: string; phone?: string };
  shipping: { address: string; city: string; postalCode: string; country: string; methodId: string };
}

export interface CheckoutResult {
  checkoutUrl?: string;
  orderNumber?: string;
  requiresManualPayment?: boolean;
}

/** Charge utile du formulaire de contact public. */
export interface ContactPayload {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
}

/**
 * Extrait la collection d'une réponse d'API.
 *
 * Le backend n'a pas de forme unique : /api/projects et /api/events/type/:type
 * renvoient un tableau nu, tandis que /api/events, /api/products et
 * /api/services renvoient un objet paginé { events: [...], pagination, filters }.
 *
 * Les pages consommaient ces réponses comme des tableaux. Sur les routes
 * paginées, `data.map(...)` levait une exception aussitôt avalée par le bloc
 * catch, et la page s'affichait vide — sans erreur visible. C'est ce qui
 * masquait les formations, les compétitions, la boutique et les services.
 */
const unwrapCollection = (data: unknown, key: string): any[] => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const collection = (data as Record<string, unknown>)[key];
    if (Array.isArray(collection)) return collection;
    // Repli : première valeur de type tableau, si la clé attendue change.
    const fallback = Object.values(data).find(Array.isArray);
    if (fallback) return fallback as any[];
  }
  return [];
};

// Service API centralisé avec gestion d'erreurs
class ApiService {
  /** Enveloppe une réponse en normalisant sa collection. */
  private async fetchCollection(url: string, key: string): Promise<ApiResponse<any[]>> {
    const response = await this.fetchWithErrorHandling<any>(url);
    if (!response.success) return { error: response.error, success: false };
    return { data: unwrapCollection(response.data, key), success: true };
  }

  private async fetchWithErrorHandling<T>(url: string, options?: RequestInit): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...options?.headers,
        },
        ...options,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return { data, success: true };
    } catch (error) {
      console.error('API Error:', error);
      return {
        error: error instanceof Error ? error.message : 'Erreur de connexion à l\'API',
        success: false
      };
    }
  }

  // Services
  async getServices() {
    return this.fetchCollection(buildApiUrl(API_CONFIG.ENDPOINTS.SERVICES), 'services');
  }

  // Events
  async getEvents() {
    return this.fetchCollection(buildApiUrl(API_CONFIG.ENDPOINTS.EVENTS), 'events');
  }

  async getEventsByType(type: string) {
    return this.fetchCollection(buildApiUrl(API_CONFIG.ENDPOINTS.EVENTS_BY_TYPE(type)), 'events');
  }

  // Products
  async getProducts() {
    return this.fetchCollection(buildApiUrl(API_CONFIG.ENDPOINTS.PRODUCTS), 'products');
  }

  // Projects
  async getProjects() {
    return this.fetchCollection(buildApiUrl(API_CONFIG.ENDPOINTS.PROJECTS), 'projects');
  }

  // Articles
  async getArticles() {
    return this.fetchCollection(buildApiUrl(API_CONFIG.ENDPOINTS.ARTICLES), 'articles');
  }

  // Settings
  async getSettings() {
    return this.fetchWithErrorHandling(buildApiUrl(API_CONFIG.ENDPOINTS.SETTINGS));
  }

  // Compteurs de la page d'accueil, calculés depuis la base à chaque appel.
  async getPublicStats() {
    return this.fetchWithErrorHandling<PublicStats>(buildApiUrl(API_CONFIG.ENDPOINTS.STATS));
  }

  // Témoignages publiés.
  async getTestimonials() {
    return this.fetchCollection(buildApiUrl(API_CONFIG.ENDPOINTS.TESTIMONIALS), 'testimonials');
  }

  // Passage de commande. Renvoie soit { checkoutUrl } (redirection Stripe),
  // soit { requiresManualPayment } si le paiement en ligne n'est pas configuré.
  async checkout(payload: CheckoutPayload) {
    return this.fetchWithErrorHandling<CheckoutResult>(buildApiUrl(API_CONFIG.ENDPOINTS.CHECKOUT), {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // Envoi du formulaire de contact public.
  async sendContact(payload: ContactPayload) {
    return this.fetchWithErrorHandling(buildApiUrl('/contacts'), {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // Inscription à la newsletter.
  async subscribeNewsletter(email: string) {
    return this.fetchWithErrorHandling(buildApiUrl('/newsletter/subscribe'), {
      method: 'POST',
      body: JSON.stringify({ email, source: 'site-public' }),
    });
  }

  // Health check
  async checkHealth() {
    return this.fetchWithErrorHandling(buildApiUrl(API_CONFIG.ENDPOINTS.HEALTH));
  }
}

// Instance singleton
export const apiService = new ApiService();